import { useEffect, useMemo, useState } from 'react';
import type { CatalogItem, CatalogItemModifierOption } from '@matix/types';
import { useLang } from '../../lib/language';
import { nameFor } from '../../lib/localizedName';
import { useMoneyFormatter } from './hooks';
import type { CallCenterCartLine } from './store';

/**
 * Choose an item's modifiers before it joins the basket.
 *
 * A separate, smaller dialog than the till's `ModifierModal` rather than a
 * reuse: that one reads and writes `useCart`, which the agent surface
 * deliberately does not have. The RULES are the same and are the point —
 * `min_selections` / `max_selections` come off the item and are enforced here,
 * so an agent cannot send the kitchen a burger with no bun choice.
 *
 * Opens automatically for any item that HAS groups, and never for one that does
 * not: a dialog that appears on every tap to say "nothing to choose" is a
 * dialog an agent learns to dismiss before reading.
 */
export function ModifierPicker({
  item,
  onCancel,
  onConfirm,
}: {
  item: CatalogItem | null;
  onCancel: () => void;
  onConfirm: (opts: { qty: number; modifiers: CallCenterCartLine['modifiers']; notes: string | null }) => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();

  const groups = useMemo(() => item?.modifier_groups ?? [], [item]);
  const [selected, setSelected] = useState<Record<number, number[]>>({});
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  // Pre-tick the defaults, which is what the customer gets if the agent says
  // nothing — and is how a "regular size" group is meant to behave.
  useEffect(() => {
    if (!item) return;
    const seed: Record<number, number[]> = {};
    for (const g of item.modifier_groups ?? []) {
      const defaults = g.options.filter((o) => o.is_default && o.is_active).map((o) => o.id);
      if (defaults.length > 0) seed[g.id] = g.max_selections === 1 ? [defaults[0]!] : defaults;
    }
    setSelected(seed);
    setQty(1);
    setNotes('');
  }, [item]);

  if (!item) return null;

  function toggle(groupId: number, option: CatalogItemModifierOption, max: number): void {
    setSelected((prev) => {
      const current = prev[groupId] ?? [];
      const on = current.includes(option.id);
      // A single-choice group REPLACES rather than refusing: tapping a second
      // size means "that one instead", and making the agent untick first is a
      // step they will resent forty times a shift.
      if (max === 1) return { ...prev, [groupId]: on ? [] : [option.id] };
      if (on) return { ...prev, [groupId]: current.filter((id) => id !== option.id) };
      if (current.length >= max) return prev;
      return { ...prev, [groupId]: [...current, option.id] };
    });
  }

  const chosen = groups.flatMap((g) =>
    (selected[g.id] ?? []).flatMap((id) => {
      const opt = g.options.find((o) => o.id === id);
      return opt
        ? [{
            option_id: opt.id,
            group_id: g.id,
            group_name: g.name,
            name: opt.name,
            name_ar: opt.name_ar,
            price_delta: opt.price_delta,
          }]
        : [];
    }),
  );

  const unmet = groups.filter((g) => (selected[g.id] ?? []).length < g.min_selections);
  const delta = chosen.reduce((sum, m) => sum + m.price_delta, 0);
  const unit = (item.selling_price ?? 0) + delta;

  return (
    <div className="cc-modal-backdrop" onClick={onCancel}>
      <div className="cc-modal cc-modal--wide" onClick={(e) => e.stopPropagation()}>
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">
              {nameFor(lang, item, `#${item.id}`)}
            </div>
            <div className="cc-modal-sub">{money(item.selling_price ?? 0)}</div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onCancel}>×</button>
        </header>

        <div className="cc-mod-groups">
          {groups.map((g) => {
            const picks = selected[g.id] ?? [];
            const rule =
              g.min_selections > 0 && g.max_selections === 1
                ? t({ en: 'Choose 1', ar: 'اختر واحداً' })
                : g.min_selections > 0
                  ? t({ en: `Choose at least ${g.min_selections}`, ar: `اختر ${g.min_selections} على الأقل` })
                  : t({ en: `Up to ${g.max_selections}`, ar: `حتى ${g.max_selections}` });
            return (
              <section key={g.id} className="cc-mod-group">
                <h4>
                  {nameFor(lang, g, `#${g.id}`)}
                  <span className={`cc-mod-rule ${picks.length < g.min_selections ? 'cc-mod-rule--unmet' : ''}`}>
                    {rule}
                  </span>
                </h4>
                <div className="cc-mod-options">
                  {g.options
                    .filter((o) => o.is_active)
                    .map((o) => {
                      const on = picks.includes(o.id);
                      return (
                        <button
                          key={o.id}
                          type="button"
                          className={`cc-mod-option ${on ? 'cc-mod-option--on' : ''}`}
                          onClick={() => toggle(g.id, o, g.max_selections)}
                        >
                          <span>{nameFor(lang, o, `#${o.id}`)}</span>
                          {o.price_delta !== 0 && (
                            <em>{o.price_delta > 0 ? '+' : ''}{money(o.price_delta)}</em>
                          )}
                        </button>
                      );
                    })}
                </div>
              </section>
            );
          })}
        </div>

        <label className="cc-field">
          <span>{t({ en: 'Note for the kitchen', ar: 'ملاحظة للمطبخ' })}</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="cc-mod-foot">
          <div className="cc-qty">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
            <span>{qty}</span>
            <button type="button" onClick={() => setQty((q) => q + 1)}>+</button>
          </div>
          <button
            type="button"
            className="cc-send"
            disabled={unmet.length > 0}
            onClick={() => onConfirm({ qty, modifiers: chosen, notes: notes.trim() || null })}
          >
            {unmet.length > 0
              ? t({ en: 'Choose the required options', ar: 'اختر الخيارات المطلوبة' })
              : `${t({ en: 'Add', ar: 'إضافة' })} · ${money(unit * qty)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
